import COLORS from '../core/colors.json';
import PRIMARY_COLOR from './primary-color.json';
import { hexToRgbChannel, createPaletteChannel } from '../styles';
import { primary as corePrimary, grey as coreGreyPalette } from '../core/palette';
import { BRAND_COLORS } from '../theme';
import { createShadowColor, customShadows as coreCustomShadows } from '../core/custom-shadows';
import { getThemePalette, applyThemePalette } from '../../components/common/settings/theme-utils';

// ----------------------------------------------------------------------

const PRIMARY_COLORS = {
  default: COLORS.primary,
  cyan: PRIMARY_COLOR.cyan,
  purple: PRIMARY_COLOR.purple,
  blue: PRIMARY_COLOR.blue,
  orange: PRIMARY_COLOR.orange,
  red: PRIMARY_COLOR.red,
};

// ----------------------------------------------------------------------

/**
 * [1] settings @primaryColor
 * [2] settings @contrast
 */

export function updateCoreWithSettings(theme, settings) {
  const { colorSchemes, customShadows } = theme;

  // Use primary color from settings, fallback to 'default'
  const primary_color = settings?.primaryColor || 'default';

  const updatedPrimary = getPalette(
    primary_color,
    corePrimary,
    PRIMARY_COLORS[primary_color]
  );

  // Apply theme palette if specified in settings
  let updatedTheme = {
    ...theme,
    colorSchemes: {
      ...colorSchemes,
      light: {
        palette: {
          ...colorSchemes?.light?.palette,
          /** [1] */
          primary: updatedPrimary,
          /** [2] */
          background: {
            ...colorSchemes?.light?.palette?.background,
            default: getBackgroundDefault(settings?.contrast || 'default'),
            defaultChannel: hexToRgbChannel(getBackgroundDefault(settings?.contrast || 'default')),
          },
        },
      },
      dark: {
        palette: {
          ...colorSchemes?.dark?.palette,
          /** [1] */
          primary: updatedPrimary,
          /** [2] - Apply contrast to dark mode too */
          background: {
            ...colorSchemes?.dark?.palette?.background,
            default: getBackgroundDefaultDark(settings?.contrast || 'default'),
            defaultChannel: hexToRgbChannel(getBackgroundDefaultDark(settings?.contrast || 'default')),
          },
        },
      },
    },
    customShadows: {
      ...customShadows,
      /** [1] */
      primary:
        primary_color === 'default'
          ? coreCustomShadows('light').primary
          : createShadowColor(updatedPrimary.mainChannel),
    },
  };

  // Apply theme palette if specified
  if (settings?.themePalette) {
    // Apply theme palette to both light and dark modes
    updatedTheme = applyThemePalette(updatedTheme, settings.themePalette, settings.colorScheme || 'light');
  }

  // Apply typography mode override
  if (settings?.typographyMode === 'blyn_v13') {
    updatedTheme.typography = {
      ...updatedTheme.typography,
      h1: { ...updatedTheme.typography.h1, fontSize: '2rem' },
      h2: { ...updatedTheme.typography.h2, fontSize: '1.5rem' },
      h3: { ...updatedTheme.typography.h3, fontSize: '1.0625rem' },
      h4: { ...updatedTheme.typography.h4, fontSize: '2.125rem' },
      h5: { ...updatedTheme.typography.h5, fontSize: '1.5rem' },
      h6: { ...updatedTheme.typography.h6, fontSize: '1.25rem' },
    };
  }

  // Expose high-contrast flag so components (e.g. DashboardCard) can respect it
  updatedTheme.highContrast = settings?.contrast === 'high';

  return updatedTheme;
}

// ----------------------------------------------------------------------

export function updateComponentsWithSettings(settings) {
  const components = {};

  /** [2] - Increased card shadow in light theme when high contrast is on */
  if (settings?.contrast === 'high') {
    const MuiCard = {
      styleOverrides: {
        root: ({ theme }) =>
          theme.palette.mode === 'light'
            ? { boxShadow: theme.customShadows.z8 }
            : {},
      },
    };

    components.MuiCard = MuiCard;
  }

  /** [2] - Increased paper shadow in light theme when high contrast is on */
  if (settings?.contrast === 'high') {
    const MuiPaper = {
      styleOverrides: {
        root: ({ theme }) =>
          theme.palette.mode === 'light'
            ? {
              boxShadow: theme.customShadows.z12,
              border: `1px solid ${theme.palette.divider}`,
            }
            : {},
      },
    };

    components.MuiPaper = MuiPaper;
  }

  return { components };
}

// ----------------------------------------------------------------------

function getPalette(name, initialPalette, updatedPalette) {
  /** [1] */
  return name === 'default' ? initialPalette : createPaletteChannel(updatedPalette);
}

function getBackgroundDefault(contrast) {
  /** [2] */
  return contrast === 'default' ? '#F7FAFB' : coreGreyPalette[200];
}

function getBackgroundDefaultDark(contrast) {
  /** [2] - Dark mode contrast */
  return contrast === 'default' ? '#0A1418' : '#1a2332';
}
